import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import T from 'i18n-react';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import Button from '@material-ui/core/Button';
import withStyles from '@material-ui/core/styles/withStyles';
import merge from 'lodash/merge';
import Chip from '@material-ui/core/Chip';

export const styles = (theme) => ({
  container: {
    padding: theme.spacing(3, 0),
    borderBottom: `1px dashed ${theme.props.border.primary.main}`,
    '& > *:nth-child(2)': {
      paddingLeft: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 0),
      '& > *:first-child': {
        paddingBottom: theme.spacing(2),
      },
      '& > *:nth-child(2)': {
        paddingLeft: theme.spacing(0),
      },
    },
  },
  title: {
    fontWeight: 'bold',
  },
  skeleton: {
    width: '100%',
    maxWidth: 300,
    height: '100%',
  },
  text: {
    whiteSpace: 'pre-wrap',
    fontSize: theme.typography.pxToRem(14),
    minHeight: `calc(${theme.typography.pxToRem(14)} * ${
      theme.typography.body1.lineHeight
    })`,
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(16),
      minHeight: `calc(${theme.typography.pxToRem(16)} * ${
        theme.typography.body1.lineHeight
      })`,
    },
  },
  textButtonGrid: {
    marginLeft: 'auto',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  titleButtonGrid: {
    marginLeft: 'auto',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  button: {
    color: theme.palette.primary.main,
    padding: 0,
    whiteSpace: 'nowrap',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  buttonStartIcon: {
    marginLeft: 0,
  },
  editButton: {
    marginLeft: theme.spacing(1),
  },
  registerButtonText: {
    color: theme.palette.text.secondary,
  },
  editButtonText: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  editContent: {},
  chip: {
    marginRight: theme.spacing(1.5),
    width: 64,
  },
});

export class InfoRow extends React.Component {
  state = {
    edit: false,
  };

  _mounted = false;
  newButtonProps = {};
  editButtonProps = {};

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  skeletonRenderer(className) {
    return (
      <Skeleton
        variant="text"
        className={clsx(this.props.classes.skeleton, className)}
      />
    );
  }

  titleRenderer() {
    const { classes, titleGridProps } = this.props;

    return (
      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        item
        xs={12}
        sm={5}
        md={4}
        component={(props) => <Typography component="div" {...props} />}
        className={classes.title}
        color="textSecondary"
        {...titleGridProps}
      >
        <Grid item xs="auto" style={{ width: '100%' }}>
          {this.titleChildrenRenderer()}
        </Grid>
        {this.displayButton() && !this.isNew() && (
          <Grid item className={classes.titleButtonGrid}>
            {this.buttonRenderer()}
          </Grid>
        )}
      </Grid>
    );
  }

  titleChildrenRenderer() {
    const { chip, classes } = this.props;
    if (chip) {
      return (
        <Grid container>
          <Grid item>
            <Chip {...chip} size="small" classes={{ root: classes.chip }} />
          </Grid>
          <Grid item>{this.props.title}</Grid>
        </Grid>
      );
    }
    return this.props.title;
  }

  isNew() {
    const { text, isNewChecker } = this.props;
    return isNewChecker?.(text);
  }

  displayButton(): boolean {
    const { loaded, editContent, onButtonClick } = this.props;
    return loaded && !!(editContent || onButtonClick);
  }

  handleButtonClick = (event) => {
    const { onButtonClick } = this.props;
    if (onButtonClick) {
      onButtonClick(event);
    } else {
      this.setState({ edit: !this.state.edit });
    }
  };

  buttonRenderer() {
    const { classes, newText, editText } = this.props;
    const { edit } = this.state;

    const buttonProps = merge(
      {},
      {
        className: clsx(classes.button, !this.isNew() && classes.editButton),
        classes: { startIcon: classes.buttonStartIcon },
        ...(edit && { style: { display: 'none' } }),
      },
      this.isNew() ? this.newButtonProps : this.editButtonProps,
    );

    return (
      <Button
        startIcon={this.isNew() ? <AddCircleOutlineIcon /> : <EditIcon />}
        onClick={this.handleButtonClick}
        {...buttonProps}
      >
        <span
          className={clsx(
            this.isNew() ? classes.registerButtonText : classes.editButtonText,
          )}
        >
          {this.isNew()
            ? newText || T.translate('registration')
            : editText || T.translate('edition')}
        </span>
      </Button>
    );
  }

  textRenderer() {
    const { classes, loaded, textGridProps } = this.props;

    return (
      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        item
        xs={12}
        sm={7}
        md={8}
        component={(props) => <Typography component="div" {...props} />}
        className={clsx('text-break-all', classes.text)}
        {...textGridProps}
      >
        {this.isNew() ? (
          <Grid item xs="auto" style={{ width: '100%' }}>
            {loaded ? this.buttonRenderer() : this.skeletonRenderer()}
          </Grid>
        ) : (
          <React.Fragment>
            <Grid item xs="auto" style={{ width: '100%' }}>
              {loaded ? this.textChildrenRenderer() : this.skeletonRenderer()}
            </Grid>
            {this.displayButton() && (
              <Grid item className={classes.textButtonGrid}>
                {this.buttonRenderer()}
              </Grid>
            )}
          </React.Fragment>
        )}
      </Grid>
    );
  }

  textChildrenRenderer() {
    return this.props.text;
  }

  editContentRenderer() {
    return this.props.editContent;
  }

  render() {
    const { classes } = this.props;
    const { edit } = this.state;

    return (
      <Grid container alignItems="center" className={classes.container}>
        {this.titleRenderer()}
        {this.textRenderer()}
        {edit && (
          <Grid item xs={12} className={classes.editContent}>
            {this.editContentRenderer()}
          </Grid>
        )}
      </Grid>
    );
  }
}

InfoRow.propTypes = {
  loaded: PropTypes.bool,
  title: PropTypes.node,
  text: PropTypes.node,
  titleGridProps: PropTypes.object,
  textGridProps: PropTypes.object,
  isNewChecker: PropTypes.func,
  newText: PropTypes.string,
  editText: PropTypes.string,
  editContent: PropTypes.node,
  onButtonClick: PropTypes.func,
  chip: PropTypes.shape({
    label: PropTypes.string,
    color: PropTypes.string,
  }),
};

export default withStyles(styles)(InfoRow);
